import type { WorkspaceProjection } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import { useWorkspaceIndexer } from '../../hooks/useWorkspaceIndexer';

interface WorkspaceIndexerStatusProps {
  projection: WorkspaceProjection;
  workspacePath: string;
}

function stateLabel(projection: WorkspaceProjection): string {
  return projection.index.progress === 'indexing' ? `Indexing ${projection.index.indexed}` : 'Idle';
}

function stateBadgeColor(projection: WorkspaceProjection): string {
  if (projection.index.health === 'degraded') {
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  }
  return projection.index.progress === 'indexing'
    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
}

export function WorkspaceIndexerStatus({ projection, workspacePath }: WorkspaceIndexerStatusProps) {
  const canIndexLocally =
    projection.assignment.routing_class === 'local' && workspacePath.length > 0;
  const { isStarting, error, reindex } = useWorkspaceIndexer(
    canIndexLocally
      ? {
          workspaceId: projection.workspace_id,
          workspacePath,
          assignmentEpoch: projection.assignment.epoch,
        }
      : null
  );
  const isBusy = isStarting || projection.index.progress === 'indexing';

  return (
    <div className="space-y-1" data-testid="workspace-indexer-status">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Search Index</span>
          <span
            className={`rounded px-1.5 py-0.5 text-xs font-medium ${stateBadgeColor(projection)}`}
          >
            {stateLabel(projection)}
          </span>
        </div>
        {canIndexLocally && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            disabled={isBusy}
            onClick={(event) => {
              event.stopPropagation();
              void reindex();
            }}
            data-testid="reindex-button"
          >
            Re-index
          </Button>
        )}
      </div>

      <p
        className={
          projection.index.health === 'degraded'
            ? 'text-xs text-destructive'
            : 'text-xs text-muted-foreground'
        }
        data-testid="workspace-index-health"
        data-health={projection.index.health}
        data-progress={projection.index.progress}
      >
        {projection.index.health === 'degraded'
          ? 'Search is behind; a full re-index is required'
          : projection.index.progress === 'indexing'
            ? 'Search is catching up'
            : 'Search is current'}
      </p>

      {projection.index.indexed > 0 && (
        <p className="text-xs text-muted-foreground">{projection.index.indexed} files indexed</p>
      )}

      {error && (
        <p className="text-xs text-destructive" data-testid="workspace-index-start-error">
          {error.message}
        </p>
      )}
    </div>
  );
}
